/*
 OK FBI — Private Investigator Inbox drawer (VIP+STREAMER only).

 Cinematic sliding drawer with unread badge. Polls every 3s for new messages
 while open. Server enforces recipient-only access — the drawer never
 references another investigator's data.
*/
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/pages/AuthPage";
import { useSession } from "@/contexts/SessionContext";
import { Mail, X, Lock, MessageSquare, AlertTriangle, ShieldAlert, FileText } from "lucide-react";

const TYPE_ICON = {
  SECRET_CLUE: MessageSquare, WARNING: AlertTriangle, THREAT: ShieldAlert,
  ANONYMOUS_MESSAGE: MessageSquare, WITNESS_TIP: MessageSquare,
  PRIVATE_EVIDENCE: FileText, EVIDENCE_IMAGE: FileText, DOCUMENT: FileText,
  AUDIO: MessageSquare, VOICE_MESSAGE: MessageSquare, FORENSIC_RESULT: FileText,
  SUSPICIOUS_LEAD: MessageSquare, CHARACTER_CONTACT: MessageSquare,
  CONFIDENTIAL_FILE: FileText, MISDIRECTION: MessageSquare,
  URGENT_EVENT: AlertTriangle, SYSTEM_EVENT: MessageSquare,
};

const INTENSITY_COLOR = {
  LOW: "text-neutral-300 border-neutral-800",
  MEDIUM: "text-yellow-300 border-yellow-800/60",
  HIGH: "text-red-300 border-red-800/60",
};

export default function InvestigatorInboxDrawer() {
  const { user } = useAuth();
  const { session } = useSession();
  const caseId = session?.current_case_id;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState(null);
  const pollRef = useRef(null);

  const isVipOrHigher = useMemo(() => {
    const p = (user?.membership_plan || "").toUpperCase();
    return ["VIP", "LEGENDARY", "ROYAL", "PREMIUM", "STREAMER", "OWNER"].includes(p) || user?.role === "OWNER";
  }, [user]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/inbox", { params: caseId ? { case_id: caseId } : {} });
      setMessages(data?.messages || []);
      setLocked(!!data?.locked);
    } catch { /* silent */ }
    try {
      const { data } = await api.get("/inbox/unread-count", { params: caseId ? { case_id: caseId } : {} });
      setUnread(data?.unread || 0);
    } catch {}
  }, [user, caseId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!open) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(load, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, load]);

  // Hide entirely on public/overlay routes
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (!user) return null;
  if (path.startsWith("/overlay") || path === "/" || path === "/login"
      || path.startsWith("/terms") || path.startsWith("/privacy")) return null;

  const openMessage = async (m) => {
    setSelected(m);
    try {
      await api.post(`/inbox/mark-read/${m.message_id}`);
      setUnread(u => Math.max(0, u - (m.read_at ? 0 : 1)));
    } catch {}
  };

  return createPortal((
    <>
      {/* Floating FAB */}
      <button
        data-testid="inbox-fab"
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-24 z-40 bg-black/80 border border-yellow-800/60 text-yellow-300 rounded-full w-12 h-12 flex items-center justify-center backdrop-blur hover:bg-yellow-950/40 transition"
        aria-label="الرسائل السرية">
        <Mail size={18}/>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-mono rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex" dir="rtl">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}/>
          <div data-testid="inbox-drawer"
               className="ml-auto relative bg-neutral-950 border-l border-yellow-900/40 w-full max-w-md h-full overflow-y-auto shadow-2xl">
            <div className="p-4 border-b border-neutral-900 flex items-center justify-between sticky top-0 bg-neutral-950/95 backdrop-blur">
              <div>
                <div className="text-[10px] tracking-[0.35em] text-yellow-500/70 font-cairo">CLASSIFIED · INBOX</div>
                <div className="text-lg font-cairo text-yellow-300 mt-0.5">صندوق الرسائل السرية</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white p-1" data-testid="inbox-close">
                <X size={20}/>
              </button>
            </div>

            {locked && (
              <div className="p-6 text-center">
                <Lock className="mx-auto text-yellow-500 mb-3" size={28}/>
                <div className="font-cairo text-yellow-300">صندوق الرسائل السرية</div>
                <div className="text-xs font-cairo text-neutral-400 mt-2">
                  متاح لباقة VIP فأعلى — رسائل شخصية، تلميحات سرية، وأدلة خاصة.
                </div>
                <a href="/plans" className="mt-4 inline-block px-4 py-2 border border-yellow-800/60 text-yellow-300 rounded text-xs font-cairo tracking-widest">
                  استعراض الباقات
                </a>
              </div>
            )}

            {!locked && messages.length === 0 && (
              <div className="p-8 text-center text-sm text-neutral-500 font-cairo">
                لا يوجد رسائل بعد. تابع تحقيقك — سيصلك ما يهمّك في الوقت المناسب.
              </div>
            )}

            {!locked && !selected && messages.map((m) => {
              const Icon = TYPE_ICON[m.type] || MessageSquare;
              const iCol = INTENSITY_COLOR[m.intensity] || INTENSITY_COLOR.LOW;
              return (
                <button key={m.message_id}
                        data-testid={`inbox-item-${m.message_id}`}
                        onClick={() => openMessage(m)}
                        className={`w-full text-right p-4 border-b border-neutral-900 hover:bg-neutral-900/40 transition flex gap-3 items-start ${!m.read_at ? "bg-yellow-950/10" : ""}`}>
                  <Icon size={16} className={`mt-1 ${iCol.split(" ")[0]}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-cairo text-yellow-200 truncate">{m.title || m.type}</div>
                      {!m.read_at && <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0"/>}
                    </div>
                    <div className="text-xs text-neutral-400 font-cairo mt-1 line-clamp-2">{m.body}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] font-cairo px-1.5 py-0.5 rounded border ${iCol}`}>{m.type}</span>
                      {m.intensity && (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${iCol}`}>{m.intensity}</span>
                      )}
                      <span className="text-[10px] text-neutral-600 font-mono">{(m.created_at||"").slice(0,16).replace("T"," ")}</span>
                    </div>
                  </div>
                </button>
              );
            })}

            {selected && (
              <div className="p-6" data-testid="inbox-detail">
                <button onClick={() => setSelected(null)}
                        className="text-xs text-yellow-400 font-cairo mb-4">← رجوع</button>
                <div className="text-[10px] tracking-widest text-neutral-500 font-mono">{selected.type} · {selected.intensity || "—"}</div>
                <div className="text-xl font-cairo text-yellow-200 mt-1">{selected.title || selected.type}</div>
                <div className="text-xs text-neutral-500 font-mono mt-1">{selected.created_at}</div>
                <div className="mt-4 font-cairo text-neutral-200 leading-relaxed whitespace-pre-wrap">{selected.body}</div>
                {selected.private_evidence_grant && (
                  <div className="mt-6 border border-yellow-800/60 rounded p-3 bg-yellow-950/20">
                    <div className="text-xs font-cairo text-yellow-300">تم منحك دليلاً خاصاً</div>
                    <div className="text-xs font-mono text-neutral-400 mt-1">{selected.private_evidence_grant}</div>
                    <div className="text-[11px] font-cairo text-neutral-500 mt-2">
                      يمكنك استخدامه في الاستجواب من زر "مواجهة بالدليل".
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  ), document.body);
}
